import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import WeeklyPlan from "./pages/WeeklyPlan";
import ShoppingList from "./pages/ShoppingList";
import SOSDinner from "./pages/SOSDinner";
import CookMode from "./pages/CookMode";
import LittleWins from "./pages/LittleWins";
import MillieMemory from "./pages/MillieMemory";
import Plans from "./pages/Plans";
import ForgotPassword, { ResetPassword } from "./pages/ForgotPassword";
import PaymentSuccess, { PaymentCancel } from "./pages/PaymentResult";
import KidMode from "./pages/KidMode";
import InstallPrompt from "./components/InstallPrompt";
import Profile from "./pages/Profile";
import AskMillie from "./pages/AskMillie";
import Kitchen from "./pages/Kitchen";
import Settings from "./pages/Settings";
import HelperSetup from "./pages/HelperSetup";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Landing from "./pages/Landing";
import Recipes from "./pages/Recipes";
import AuthCallback from "./pages/AuthCallback";
import Demo from "./pages/Demo";
import DemoBanner from "./components/DemoBanner";

function Splash() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF6F1] gap-3" data-testid="app-splash">
      <img src="/icon-512.png" alt="Sorted" width={120} height={120} className="rounded-[26%] shadow-soft animate-in fade-in zoom-in-95 duration-500" />
      <p className="font-hand text-3xl text-sorted-rose">Sorted</p>
      <p className="text-xs font-semibold text-sorted-slate">Getting things sorted…</p>
    </div>
  );
}

function Protected({ children, requireHousehold = true }) {
  const { user, households } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (requireHousehold && households.length === 0) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, households } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={households.length ? "/home" : "/onboarding"} replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google OAuth returns to {origin}/home#session_id=... — handle it first.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <InstallPrompt />
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/demo" element={<PublicOnly><Demo /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route path="/onboarding" element={<Protected requireHousehold={false}><Onboarding /></Protected>} />
      <Route path="/home" element={<Protected><Home /></Protected>} />
      <Route path="/meals" element={<Protected><WeeklyPlan /></Protected>} />
      <Route path="/millie" element={<Protected><AskMillie /></Protected>} />
      <Route path="/kitchen" element={<Protected><Kitchen /></Protected>} />
      <Route path="/shopping" element={<Protected><ShoppingList /></Protected>} />
      <Route path="/recipes" element={<Protected><Recipes /></Protected>} />
      <Route path="/sos" element={<Protected><SOSDinner /></Protected>} />
      <Route path="/cook/sos/:sosId" element={<Protected><CookMode /></Protected>} />
      <Route path="/cook/:day" element={<Protected><CookMode /></Protected>} />
      <Route path="/wins" element={<Protected><LittleWins /></Protected>} />
      <Route path="/memory" element={<Protected><MillieMemory /></Protected>} />
      <Route path="/cook/:day/kids" element={<Protected><KidMode /></Protected>} />
      <Route path="/profile" element={<Protected requireHousehold={false}><Profile /></Protected>} />
      <Route path="/plans" element={<Protected requireHousehold={false}><Plans /></Protected>} />
      <Route path="/payment/success" element={<Protected requireHousehold={false}><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected requireHousehold={false}><PaymentCancel /></Protected>} />
      <Route path="/settings" element={<Protected requireHousehold={false}><Settings /></Protected>} />
      <Route path="/helper-setup" element={<Protected requireHousehold={false}><HelperSetup /></Protected>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <DemoBanner />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
